/**
 * Job type and status, as the GraphQL schema actually declares them.
 *
 * Mirrors the backend enums in `src/main/resources/graphql/schema.graphqls`:
 *
 *     enum JobType   { FULL_TIME, PART_TIME, CONTRACT, FREELANCE, INTERNSHIP }
 *     enum JobStatus { OPEN, CLOSED, EXPIRED }
 *
 * **The value IS the wire value; the label is display only.** That distinction is the whole
 * reason this file exists. `JobWorkspace` used to build its dropdowns from human strings
 * (`'Full Time'`, `'On hold'`, `'Open'`, `'Filled'`) and `updateJobDataById` interpolated the
 * chosen one UNQUOTED, as a GraphQL enum literal. Both halves were broken:
 *
 *   - `jobType: Full Time` is a *parse error* -- an identifier cannot contain a space -- so the
 *     whole mutation was refused and every card on the page silently failed to save.
 *   - `jobStatus: Open` parses but is not a member of `{OPEN, CLOSED, EXPIRED}`, so it failed
 *     validation instead.
 *   - Read side: a job stored as `FULL_TIME` matched no option labelled `'Full Time'`, so the
 *     select rendered blank while the underlying value was perfectly fine. That blank box was
 *     the visible symptom people reported.
 *
 * `'On hold'` and `'Filled'` are deliberately absent. `JobStatus` has no equivalent, so they
 * could never be stored: any save carrying one was rejected before it reached Mongo. Nothing is
 * lost by dropping them. They are NOT aliased onto `CLOSED` either -- that would silently discard
 * the distinction the admin was trying to express and invent a vocabulary only this app knows.
 * If Ops need those states they are a schema change, with a decision about what an on-hold job
 * does to public search and alerts.
 */

export interface EnumOption {
  /** The value sent over the wire. Must be a legal member of the schema enum. */
  value: string;
  /** Display text. Never sent anywhere. */
  label: string;
}

export const JOB_TYPES: EnumOption[] = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'FREELANCE', label: 'Freelance' },
  { value: 'INTERNSHIP', label: 'Internship' },
];

export const JOB_STATUSES: EnumOption[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'EXPIRED', label: 'Expired' },
];

const JOB_TYPE_VALUES = new Set(JOB_TYPES.map((o) => o.value));
const JOB_STATUS_VALUES = new Set(JOB_STATUSES.map((o) => o.value));

/**
 * `'Full Time'` / `'full-time'` / `'full_time'` -> `'FULL_TIME'`, and anything that is not a
 * member -> `undefined`.
 *
 * Returning `undefined` rather than a fallback is deliberate and load-bearing: the mutation
 * builder OMITS a key whose enum normalises to `undefined`, and the backend merges with
 * `Optional.ofNullable(...).ifPresent(...)`, so an absent key preserves whatever is stored. A
 * value we cannot make sense of therefore changes nothing, instead of writing a guess or
 * producing a document that will not parse.
 *
 * This is the same normalisation the public UI has always done inline
 * (`(j.jobType || 'FULL_TIME').replace(/ /g, '_').toUpperCase()`), given a name so both sides
 * can share one rule rather than two that drift.
 */
const normalise = (v: unknown): string =>
  String(v ?? '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

export const toJobTypeEnum = (v: unknown): string | undefined => {
  const n = normalise(v);
  return JOB_TYPE_VALUES.has(n) ? n : undefined;
};

export const toJobStatusEnum = (v: unknown): string | undefined => {
  const n = normalise(v);
  return JOB_STATUS_VALUES.has(n) ? n : undefined;
};

/**
 * Enum value -> display text, for anything that has to render one.
 *
 * Falls back to title-casing the raw value rather than showing an empty cell, so a legacy or
 * unexpected value is still legible (and visibly odd) instead of vanishing.
 */
const prettify = (v: unknown): string =>
  String(v ?? '')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const labelFrom = (options: EnumOption[], v: unknown): string => {
  const n = normalise(v);
  return options.find((o) => o.value === n)?.label ?? prettify(v);
};

export const jobTypeLabel = (v: unknown): string => labelFrom(JOB_TYPES, v);
export const jobStatusLabel = (v: unknown): string => labelFrom(JOB_STATUSES, v);

/**
 * Experience level -- the one field that answers "how senior is this role".
 *
 * Unlike `JobType` and `JobStatus` this is **not** a GraphQL enum: `Job.experience` is a plain
 * `String`, so nothing on the wire rejects a bad value. The constraint has to live here, and it
 * has to be a select rather than a text box, because the stored string is matched *exactly* by
 * three separate consumers:
 *
 *   - the public job filter, which passes `{key: 'experience', value}` straight through to a
 *     Mongo match in `JobsAggregateService`;
 *   - the `/jobs/experience/[level]` SEO landing pages, which are generated from this list;
 *   - the analytics grouping dimension.
 *
 * So a job whose experience reads `'3-5 years'` -- which a free-text box invited, and which is
 * what this control used to be -- is not merely untidy. It is invisible to every experience
 * filter and absent from its landing page, while still looking filled-in to the admin who typed
 * it. Value and label are identical here because the display string IS the stored string.
 *
 * Mirrors `EXPERIENCE_ENUM` in the backend's `OpenAIConstants` (which constrains AI extraction)
 * and `EXPERIENCE_LEVELS` in the public site's `src/lib/constants.ts`.
 */
export const EXPERIENCE_LEVELS: EnumOption[] = [
  { value: 'Entry/Fresher', label: 'Entry/Fresher' },
  { value: 'Mid-Level', label: 'Mid-Level' },
  { value: 'Sr-Level', label: 'Sr-Level' },
  { value: 'Management-Level', label: 'Management-Level' },
];

/**
 * The years -> level mapping the AI extraction prompt already applies, so a human editing a job
 * by hand lands on the same band the extractor would have chosen for it.
 *
 * Deliberately advisory, never automatic: `Management-Level` is about scope, not tenure. An
 * eight-year individual contributor is `Sr-Level` and an eight-year head of retail is
 * `Management-Level`, and no function of years alone can tell those apart. This is used to
 * *suggest* when the two fields disagree, and the admin decides.
 */
export const experienceLevelForYears = (years: number | null | undefined): string | undefined => {
  if (years == null || Number.isNaN(years) || years < 0) return undefined;
  if (years <= 1) return 'Entry/Fresher';
  if (years <= 5) return 'Mid-Level';
  return 'Sr-Level';
};
